import {
  Box,
  Typography,
  Chip,
  Paper,
  Checkbox,
  InputAdornment,
  TextField,
} from "@mui/material";
import { useCallback, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faBoxesPacking,
  faBoxOpen,
} from "@fortawesome/free-solid-svg-icons";
import Button from "../../../../../components/Button";
import { VolumesList } from "./VolumesList";
import { Virtuoso } from "react-virtuoso";
import { VolumeBipedRow } from "./VolumeBipedRow";
import { useVolumeManagement } from "./hooks/useVolumeManagement";
import { v4 as uuidv4 } from "uuid";

export const AddVolumeForm = ({
  bipeds,
  volumes,
  setVolumes,
  handleRemoveVolume,
  handleRemoveItem,
  handleEditVolume,
  mode = "create", // 'create' ou 'edit'
}) => {
  const {
    localVolumeName,
    setLocalVolumeName,
    searchTerm,
    setSearchTerm,
    selectedVolumeName,
    setSelectedVolumeName,
    selectedItems,
    quantities,
    activeTab,
    setActiveTab,
    allBipedItems,
    filteredItems,
    allSelected,
    hasUnallocatedItems,
    handleSelectAll,
    handleToggleSelectItem,
    handleQuantityChange,
    handleCreateVolume,
    handleAddSingleItem,
    handleAddSelectedToVolume,
    handleQuantityKeyDown,
  } = useVolumeManagement({ bipeds, volumes, setVolumes, mode });

  const [newVolumeInlineName, setNewVolumeInlineName] = useState("");

  const handleQuickCreateVolume = () => {
    const trimmed = newVolumeInlineName.trim() || localVolumeName.trim();
    if (!trimmed) {
      return;
    }

    const exists = volumes.some(
      (v) => v.name.toLowerCase() === trimmed.toLowerCase(),
    );
    if (exists) {
      return;
    }

    const newVolObj = {
      identifier: uuidv4(),
      name: trimmed,
      items: [],
    };

    setVolumes((prev) => [...prev, newVolObj]);
    setSelectedVolumeName(trimmed);
    setLocalVolumeName("");
    setNewVolumeInlineName("");
  };

  const renderRowItem = useCallback(
    (index) => {
      const item = filteredItems[index];
      const isSelected = selectedItems.has(item.id);
      const canSelect =
        item.type === "equipment" ? !item.isAllocated : !item.isFullyAllocated;

      return (
        <VolumeBipedRow
          key={item.id}
          item={item}
          isSelected={isSelected}
          canSelect={canSelect}
          selectedVolumeName={selectedVolumeName}
          quantityValue={quantities[item.id]}
          handleToggleSelectItem={handleToggleSelectItem}
          handleQuantityChange={handleQuantityChange}
          handleQuantityKeyDown={handleQuantityKeyDown}
          handleAddSingleItem={handleAddSingleItem}
        />
      );
    },
    [
      filteredItems,
      selectedItems,
      selectedVolumeName,
      quantities,
      handleToggleSelectItem,
      handleQuantityChange,
      handleQuantityKeyDown,
      handleAddSingleItem,
    ],
  );

  return (
    <Box sx={{ display: "flex", gap: 3, p: 1, height: "70vh" }}>
      {/* COLUNA ESQUERDA: 60% */}
      <Box
        sx={{
          width: "60%",
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}>
        {/* Só mostra criação de volume no modo 'create' */}
        {mode === "create" && (
          <Box sx={{ display: "flex", gap: 2, alignItems: "flex-end" }}>
            <TextField
              label="Nome do Novo Volume (Ex: Volume 01, Pallet B)"
              variant="outlined"
              size="small"
              value={localVolumeName}
              onChange={(e) => setLocalVolumeName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleCreateVolume()}
              sx={{ flex: 1 }}
            />
            <Button text="Criar Volume" onClick={handleCreateVolume} />
          </Box>
        )}

        {/* Botão para criar volume no modo edit - MELHORADO */}
        {mode === "edit" && (
          <Box sx={{ display: "flex", gap: 2, alignItems: "flex-end" }}>
            <TextField
              label="Nome do Novo Volume (Ex: Volume 01, Pallet B)"
              variant="outlined"
              size="small"
              value={newVolumeInlineName}
              onChange={(e) => setNewVolumeInlineName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleQuickCreateVolume()}
              sx={{ flex: 1 }}
            />
            <Button text="Criar Volume" onClick={handleQuickCreateVolume} />
          </Box>
        )}

        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", my: 0.5 }}>
          <Typography
            variant="body2"
            fontWeight="bold"
            sx={{ alignSelf: "center", mr: 1 }}>
            Volume Atual:
          </Typography>
          {volumes.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 0.5 }}>
              {mode === "edit"
                ? "Nenhum volume. Crie um novo volume abaixo."
                : "Nenhum volume criado. Crie um volume acima."}
            </Typography>
          ) : (
            volumes.map((vol) => (
              <Chip
                key={vol.identifier}
                label={vol.name}
                disabled={!!vol.hasShipment}
                onClick={() => setSelectedVolumeName(vol.name)}
                color={selectedVolumeName === vol.name ? "primary" : "default"}
                variant={
                  selectedVolumeName === vol.name ? "filled" : "outlined"
                }
                sx={{ cursor: "pointer", fontWeight: "bold" }}
              />
            ))
          )}
        </Box>

        {/* COMPONENTES DE FILTRO (BUSCA E ABAS) */}
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <TextField
            placeholder="Buscar por item, código ou ID..."
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ flex: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FontAwesomeIcon icon={faSearch} style={{ color: "#aaa" }} />
                </InputAdornment>
              ),
            }}
          />

          <Button
            text="Adicionar Selecionados"
            onClick={handleAddSelectedToVolume}
            disabled={selectedItems.size === 0 || !selectedVolumeName}
            sx={{ height: 40 }}
          />
        </Box>

        {/* Aviso de itens não alocados no modo edit */}
        {mode === "edit" && hasUnallocatedItems && (
          <Box
            sx={{
              p: 1.5,
              backgroundColor: "#fff3cd",
              border: "1px solid #ffc107",
              borderRadius: 1,
              color: "#856404",
              fontSize: "0.875rem",
              fontWeight: "bold",
            }}>
            ⚠️ Existem itens não alocados. Todos os itens devem estar em algum
            volume.
          </Box>
        )}

        {/* ABAS (TABS CUSTOM) */}
        <Box sx={{ display: "flex", borderBottom: "1px solid #ddd", gap: 1 }}>
          <Box
            onClick={() => setActiveTab("all")}
            sx={{
              px: 3,
              py: 1,
              cursor: "pointer",
              fontWeight: "bold",
              fontSize: "0.9rem",
              borderBottom:
                activeTab === "all"
                  ? "3px solid #1976d2"
                  : "3px solid transparent",
              color: activeTab === "all" ? "#1976d2" : "#666",
            }}>
            Todos ({allBipedItems.length})
          </Box>
          <Box
            onClick={() => setActiveTab("equipment")}
            sx={{
              px: 3,
              py: 1,
              cursor: "pointer",
              fontWeight: "bold",
              fontSize: "0.9rem",
              borderBottom:
                activeTab === "equipment"
                  ? "3px solid #9c27b0"
                  : "3px solid transparent",
              color: activeTab === "equipment" ? "#9c27b0" : "#666",
            }}>
            Equipamentos (
            {allBipedItems.filter((i) => i.type === "equipment").length})
          </Box>
          <Box
            onClick={() => setActiveTab("product")}
            sx={{
              px: 3,
              py: 1,
              cursor: "pointer",
              fontWeight: "bold",
              fontSize: "0.9rem",
              borderBottom:
                activeTab === "product"
                  ? "3px solid #009688"
                  : "3px solid transparent",
              color: activeTab === "product" ? "#009688" : "#666",
            }}>
            Insumos ({allBipedItems.filter((i) => i.type === "product").length})
          </Box>
        </Box>

        {/* LISTA VIRTUALIZADA DE ITENS BIPADOS */}
        <Paper
          elevation={1}
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            backgroundColor: "#fff",
            minHeight: 0, // IMPORTANTE: Permite que o flex funcione corretamente
          }}>
          {/* CABEÇALHO DA TABELA */}
          <Box
            sx={{
              display: "flex",
              px: 2,
              py: 1,
              backgroundColor: "#f5f5f5",
              fontWeight: "bold",
              borderBottom: "2px solid #ddd",
              alignItems: "center",
              gap: 2,
              flexShrink: 0, // Impede que o cabeçalho encolha
            }}>
            <Box sx={{ width: 30, display: "flex", justifyContent: "center" }}>
              <Checkbox
                checked={allSelected}
                onChange={handleSelectAll}
                size="small"
                disabled={filteredItems.length === 0}
              />
            </Box>
            <Box sx={{ flex: 1, minWidth: 200 }}>Item</Box>
            <Box sx={{ width: 100, textAlign: "center" }}>Tipo</Box>
            <Box sx={{ width: 90, textAlign: "center" }}>Disponível</Box>
            <Box sx={{ width: 80, textAlign: "center" }}>Qtd.</Box>
            <Box sx={{ width: 70 }}></Box>
          </Box>

          {/* CORPO DO SCROLL VIRTUALIZADO */}
          <Box
            sx={{
              flex: 1,
              minHeight: 0, // Permite scroll em flexbox
              overflow: "hidden", // Importante para o Virtuoso
            }}>
            {filteredItems.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexDirection: "column",
                  gap: 2,
                  py: 4,
                }}>
                <FontAwesomeIcon
                  icon={faBoxesPacking}
                  style={{ fontSize: 48, color: "#ccc" }}
                />
                <Typography color="text.secondary" variant="body1">
                  {allBipedItems.length === 0
                    ? mode === "edit"
                      ? "Nenhum item neste movimento"
                      : "Nenhum item bipado ainda"
                    : mode === "edit"
                      ? "Todos os itens estão alocados em volumes"
                      : "Todos os itens já foram adicionados aos volumes"}
                </Typography>
              </Box>
            ) : (
              <Virtuoso
                style={{ height: "100%" }}
                totalCount={filteredItems.length}
                itemContent={renderRowItem}
                overscan={200} // Melhora performance do scroll
              />
            )}
          </Box>
        </Paper>

        {/* Legenda fora do Paper para não competir por espaço */}
        <Box sx={{ mt: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            Mostrando {filteredItems.length} de {allBipedItems.length} itens
            {mode === "edit" ? " do movimento" : " bipados"}
          </Typography>
        </Box>

        <Typography variant="caption" color="text.secondary">
          Mostrando {filteredItems.length} de {allBipedItems.length} itens
          {mode === "edit" ? " do movimento" : " bipados"}
        </Typography>
      </Box>

      {/* COLUNA DIREITA: 40% */}
      <Box
        sx={{
          width: "40%",
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}>
        <Paper
          elevation={2}
          sx={{
            p: 2,
            flex: 1,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <FontAwesomeIcon icon={faBoxOpen} style={{ color: "#1976d2" }} />
            <Typography variant="h6" fontWeight="bold">
              Volumes {mode === "edit" ? "Editados" : "Cadastrados"}
            </Typography>
            <Chip
              label={volumes?.length || 0}
              size="small"
              color="primary"
              sx={{ ml: 1 }}
            />
          </Box>

          <Box sx={{ flex: 1, overflowY: "auto", pr: 1 }}>
            <VolumesList
              volumes={volumes}
              onEditVolume={handleEditVolume}
              onRemoveVolume={handleRemoveVolume}
              onRemoveItem={handleRemoveItem}
              mode={mode}
            />
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};
